// src/ConsumerApp/Screens/Account/views/RfqsView.js

import React, { useState, useMemo, useEffect } from 'react';
import { theme } from '../../../../assets/theme/theme';
import { useNavigate, useSearchParams  } from 'react-router-dom';
import { load } from '@cashfreepayments/cashfree-js';
// Real SVGs from assets/icons
import ClipboardText from '../../../../assets/icons/PClipboardText.svg';
import ShootingStar from '../../../../assets/icons/ShootingStar.svg';
import ClockCounterClockwise from '../../../../assets/icons/ClockCounterClockwise.svg';
import CaretRight from '../../../../assets/icons/CaretRight.svg';
import Phone from '../../../../assets/icons/Phone.svg';
import ChatCircle from '../../../../assets/icons/ChatCircle.svg';
import Receipt from '../../../../assets/icons/Receipt.svg';
import ToteIcon from '../../../../assets/icons/tote.svg';
import X from '../../../../assets/icons/X.svg';
import ShieldCheck from '../../../../assets/icons/ShieldCheck.svg';

// Custom Hooks
import { useGetRfqsByCustomer, useGetRfqById } from '../../../api/rfq/rfq.hooks';
import { useInitiatePayment, useVerifyPaymentStatus, useGetOrderByTransactionId } from '../../../api/payment/payment.hooks';

const IMAGE_BASE_URL =
  process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES || 'https://assets.brickbucket.in/';

export default function RfqsView() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rfqId = searchParams.get('rfq_id');

  // 1. Session Context
  const customer = useMemo(() => {
    try {
      const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, []);

  const customerId = customer?._id || customer?.id;
  const isAuthenticated = Boolean(customerId);
  // 2. Active Selection State
  const [selectedRfqId, setSelectedRfqId] = useState(null);
  // 3. Payment State
  const [paymentMethod, setPaymentMethod] = useState('ONLINE'); // 'ONLINE' | 'COD'
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // { type, message, navOrderId, displayOrderId }

  // 3. API Queries
  const {
    data: rawRfqData = [],
    isLoading: isLoadingList,
    refetch: refetchList,
    isRefetching,
  } = useGetRfqsByCustomer({ customerId, rfqs: 'all' });

  const rfqsList = useMemo(() => {
    if (Array.isArray(rawRfqData)) return rawRfqData;
    if (Array.isArray(rawRfqData?.data)) return rawRfqData.data;
    return [];
  }, [rawRfqData]);
  console.log('rfqsList', rfqsList)
  // Set initial selected RFQ when list loads
 /*  useEffect(() => {

    if (rfqsList.length > 0 && !selectedRfqId) {
      setSelectedRfqId(rfqsList[0]._id || rfqsList[0].id);
    }
  }, [rfqsList, selectedRfqId, rfqId]);
 */
 
  useEffect(() => {
  if (rfqsList.length === 0) return;

  // If RFQ ID comes from URL, select that RFQ first
  if (rfqId) {
    const matchedRfq = rfqsList.find(
      (item) => String(item._id || item.id) === String(rfqId)
    );

    if (matchedRfq) {
      setSelectedRfqId(matchedRfq._id || matchedRfq.id);
      return;
    }
  }

  // Otherwise select first RFQ
  if (!selectedRfqId) {
    setSelectedRfqId(
      rfqsList[0]._id || rfqsList[0].id
    );
  }
}, [rfqsList, rfqId]);

  // Fetch active RFQ details
  const { data: selectedRfqDetails, isLoading: isLoadingDetails } = useGetRfqById(selectedRfqId);
  //console.log('selectedRfqDetails', selectedRfqDetails)
  const initiatePaymentMutation = useInitiatePayment();
  const verifyPaymentMutation = useVerifyPaymentStatus();
  const getOrderMutation = useGetOrderByTransactionId();

  // Fallback / Normalized Detail Fields[cite: 48]
  const activeRfq = selectedRfqDetails || rfqsList.find((r) => (r._id || r.id) === selectedRfqId);
  const items = Array.isArray(activeRfq?.items) ? activeRfq.items : [];
  const totalAmount = Number(activeRfq?.total || activeRfq?.estimated_total || 0);
  const isQuoteReceived = Boolean(activeRfq?.quotation_id || activeRfq?.is_live_rfq);
  
  // Verify if payment or order is already processed to hide payment section
  const isPaymentCompleted = 
    activeRfq?.status === 'Order Placed Sucessfully' || 
    activeRfq?.status === 'Order Placed Successfully' || 
    activeRfq?.payment_status === 'Paid';

  // Formatters[cite: 49]
  const formatDate = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return isoString;
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  // 6. Payment Logic Methods
  const verifyBackendPaymentStatus = async (orderId) => {
    setIsProcessingPayment(true);
    try {
      const result = await verifyPaymentMutation.mutateAsync({
        order_id: orderId,
        customer_id: customerId,
      }); 
      
      const status = (result?.status || result?.data?.status || '').toUpperCase();

      if (['SUCCESS', 'COMPLETED', 'PAID'].includes(status)) {
        setPaymentStatus({
          type: 'success',
          navOrderId: orderId,
          message: 'Your payment was successful. Your order is confirmed!',
        });
      } else if (['PENDING', 'PROCESSING', 'IN_PROGRESS'].includes(status)) {
        setPaymentStatus({
          type: 'pending',
          navOrderId: orderId,
          message: 'Your payment is being processed by your bank. We will update you once confirmed.',
        });
      } else if (['CANCELLED', 'USER_DROPPED', 'ABORTED'].includes(status)) {
        setPaymentStatus({ type: 'failed', message: 'Payment was cancelled. No money was deducted.' });
      } else {
        setPaymentStatus({ type: 'failed', message: result?.message || result?.data?.message || 'Payment transaction failed. Please try again.' });
      }
    } catch (err) {
      setPaymentStatus({
        type: 'pending',
        navOrderId: orderId,
        message: 'Unable to verify payment instantly. Please check your order status shortly.',
      });
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleCheckout = async () => {
    if (!isAuthenticated) return;
    if (totalAmount <= 0) {
      alert('Total amount must be greater than zero to proceed.');
      return;
    }
    
    setIsProcessingPayment(true);

    try {
      // Setup payload identical to Buy Now, injecting RFQ identifiers
      const payload = {
        amount: Math.round(totalAmount),
        customerId: customerId,
        hubId: customer?.hubId || 'default',
        projectId: activeRfq?.project_id || null,
        rfq_id: activeRfq?._id, // Pass Quote ID as bucket reference
        purchase_mode: 'RFQ',
        paymentMethod: paymentMethod === 'COD' ? 'COD' : 'ONLINE',
        selected_delivery_type: activeRfq?.delivery_type || 'SCHEDULED',
        delivery_type: activeRfq?.delivery_type || 'SCHEDULED',
      };

      const res = await initiatePaymentMutation.mutateAsync(payload);
      const resData = res?.data?.data || res?.data || res;

      // 1. Cash On Delivery Flow
      if (paymentMethod === 'COD' || resData?.payment_mode === 'COD') {
        const orderId = resData?.order_id || resData?.orderId;
        setPaymentStatus({
          type: 'cod',
          navOrderId: orderId,
          message: 'Your order is confirmed! Pay in cash upon delivery.',
        });
        setIsProcessingPayment(false);
        return;
      }

      // 2. Identify Gateway
      const targetGateway = (resData?.gateway || resData?.provider || 'CASHFREE').toUpperCase();
      const currentOrderId = resData?.order_id || resData?.orderId;

      if (targetGateway === 'CASHFREE') {
        // --- WEB CASHFREE INTEGRATION ---
        const { payment_session_id } = resData;
        if (!payment_session_id) throw new Error('Invalid Cashfree session received.');

        const cashfree = await load({ mode: "sandbox" }); // Change to "production" for live
        
        cashfree.checkout({
          paymentSessionId: payment_session_id,
          redirectTarget: "_modal" 
        }).then((result) => {
          if(result.error){
            setPaymentStatus({ type: 'failed', message: result.error.message || 'Payment cancelled by user.'});
            setIsProcessingPayment(false);
          }
          if(result.paymentDetails){
            verifyBackendPaymentStatus(currentOrderId);
          }
        });

      } else {
        throw new Error(`Unsupported payment gateway: ${targetGateway}`);
      }

    } catch (err) {
      console.error('Checkout error:', err);
      setPaymentStatus({
        type: 'failed',
        message: err?.response?.data?.message || err?.message || 'Unable to complete checkout.',
      });
      setIsProcessingPayment(false);
    }
  };

  const handleOrderNavigation = async () => {
    if (paymentStatus.type === 'failed' || paymentStatus.type === 'cancelled') {
      setPaymentStatus(null);
      return; 
    }

    const targetTransactionId = paymentStatus.navOrderId || paymentStatus.displayOrderId;
    if (!targetTransactionId) {
      setPaymentStatus(null);
      return;
    }

    setIsProcessingPayment(true);
    try {
      const response = await getOrderMutation.mutateAsync({
        transaction_order_id: targetTransactionId
      });
      const realOrderId = response?.data?.order_id || response?.order_id;
      setPaymentStatus(null);
      if (realOrderId) {
        navigate(`/consumer/account/orders/${realOrderId}`);
      } else {
        navigate(`/consumer/account/orders`);
      }
    } catch (err) {
      setPaymentStatus(null);
      navigate(`/consumer/account/orders`); 
    } finally {
      setIsProcessingPayment(false);
    }
  };

  return (
    <div style={styles.container}>
      {/* ── Page Header ── */}
      <div style={styles.pageHeader}>
        <div>
          <h2 style={styles.headerTitle}>My RFQs</h2>
          <span style={styles.headerSubtitle}>
            Track commercial quote requests, supplier estimations, and bulk bids
          </span>
        </div>

      {/*   <button
          type="button"
          style={styles.refreshBtn}
          onClick={() => refetchList()}
          disabled={isRefetching}
        >
          <img
            src={ClockCounterClockwise}
            alt="refresh"
            style={{
              width: 14,
              height: 14,
              opacity: 0.7,
              animation: isRefetching ? 'spin 1s linear infinite' : 'none',
            }}
          />
          <span>{isRefetching ? 'Refreshing...' : 'Refresh'}</span>
        </button> */}
      </div>

      {/* ── Master-Detail Grid ── */}
      {isLoadingList ? (
        <div style={styles.loadingBox}>
          <span>Fetching your RFQ records...</span>
        </div>
      ) : rfqsList.length === 0 ? (
        <div style={styles.emptyBox}>
          <div style={styles.emptyIconCircle}>
            <img
              src={ClipboardText}
              alt="rfq"
              style={{
                width: 24,
                height: 24,
                filter:
                  'invert(16%) sepia(85%) saturate(6120%) hue-rotate(348deg) brightness(91%) contrast(106%)',
              }}
            />
          </div>
          <h4 style={styles.emptyTitle}>No RFQs</h4>
          <p style={styles.emptySubtitle}>
            When you request volume quotes or custom specifications, they will be listed here.
          </p>
        </div>
      ) : (
        <div style={styles.splitLayout}>
          {/* Left Master Pane: RFQ Cards List[cite: 49, 53] */}
          <div style={styles.masterListPane}>
            <span style={styles.listSectionLabel}>All Requests ({rfqsList.length})</span>

            <div style={styles.cardsStack}>
              {rfqsList.map((item, idx) => {
                const itemId = item._id || item.id || `rfq-${idx}`;
                const isSelected = selectedRfqId === itemId;
                const rfqNumber = item.rfq_number || `#${String(itemId).slice(-6).toUpperCase()}`;
                const title =
                  item.product_categories?.[0]?.product_category_name ||
                  item.title ||
                  'Bulk Procurement Request';
                const itemCount = item.total_items ?? item.items?.length ?? 1;
                const amount = item.total || item.estimated_total || item.final_total;

                return (
                  <div
                    key={itemId}
                    style={{
                      ...styles.rfqCard,
                      borderColor: isSelected ? '#C7061F' : '#CBD5E1',
                      backgroundColor: isSelected ? '#FFF8F8' : '#FFFFFF',
                      boxShadow: isSelected ? '0 0 0 1px #C7061F' : 'none',
                    }}
                    onClick={() => setSelectedRfqId(itemId)}
                  >
                    <div style={styles.rfqCardTop}>
                      <span style={styles.rfqIdBadge}>{rfqNumber}</span>
                      <div
                        style={{
                          ...styles.statusTag,
                          backgroundColor: item.quotation_id ? '#FFF1F2' : '#F8FAFC',
                          color: item.quotation_id ? '#C7061F' : '#64748B',
                        }}
                      >
                         {item.status === 'Order Placed Sucessfully' ? item.status : 'Quote Received'}
                      </div>
                    </div>

                    <span style={styles.rfqCardDate}>{formatDate(item.createdAt)}</span>

                    <div style={styles.rfqCardBottom}>
                      <span style={styles.rfqCardTitle}>
                        {title} · {itemCount} {itemCount === 1 ? 'item' : 'items'}
                      </span>
                      {amount && (
                        <strong style={styles.rfqCardAmount}>
                          ₹{Number(amount).toLocaleString('en-IN')}
                        </strong>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Detail Pane: Selected RFQ View[cite: 48] */}
          <div style={styles.detailPane}>
            {isLoadingDetails ? (
              <div style={styles.detailLoading}>
                <span>Loading request details...</span>
              </div>
            ) : !activeRfq ? (
              <div style={styles.detailEmpty}>
                <span>Select an RFQ from the list to view its complete specification</span>
              </div>
            ) : (
              <div style={styles.detailCard}>
                {/* Header Row */}
                <div style={styles.detailHeader}>
                  <div>
                    <h3 style={styles.detailTitle}>
                      {activeRfq.rfq_number || `#${String(selectedRfqId).slice(-6).toUpperCase()}`}
                    </h3>
                    <span style={styles.detailDate}>{formatDate(activeRfq.createdAt)}</span>
                  </div>

                  <div style={styles.detailActionGroup}>
                    <a
                      href="https://wa.me/916304059302"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={styles.detailHelpBtn}
                    >
                      <img src={ChatCircle} alt="chat" style={{ width: 14, height: 14 }} />
                      <span>Chat</span>
                    </a>
                    <a href="tel:18001234567" style={styles.detailHelpBtn}>
                      <img src={Phone} alt="call" style={{ width: 14, height: 14 }} />
                      <span>Call</span>
                    </a>
                  </div>
                </div>

                {/* Best Quote Alert Banner[cite: 48] */}
             {isQuoteReceived && !isPaymentCompleted &&(
  <div style={styles.quotePaymentContainer}>
    
    {/* 1. Quotation Banner */}
    <div style={styles.quoteAlertBanner}>
      <div style={styles.quoteAlertLeft}>
        <div style={styles.quoteAlertHeader}>
          <img
            src={ShootingStar}
            alt="star"
            style={styles.starIcon}
          />
          <strong style={styles.quoteAlertTitle}>Best Quotation Received</strong>
        </div>
        <span style={styles.quoteAlertPrice}>
          ₹{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
        </span>
      </div>
    </div>

    {/* 2. Payment Method Row */}
    <div style={styles.paymentMethodCard}>
      <h4 style={styles.paymentSectionTitle}>Select Payment Method</h4>
      
      <div style={styles.paymentOptionsRow}>
        <div 
          style={{...styles.paymentOptionCompact, ...(paymentMethod === 'ONLINE' ? styles.paymentOptionActive : {})}} 
          onClick={() => setPaymentMethod('ONLINE')}
        >
          <input type="radio" checked={paymentMethod === 'ONLINE'} readOnly style={styles.radioInput} />
          <span style={styles.paymentOptionText}>Pay Online (UPI, Cards)</span>
        </div>

        <div 
          style={{
            ...styles.paymentOptionCompact, 
            ...(paymentMethod === 'COD' ? styles.paymentOptionActive : {}), 
            ...(totalAmount > 10000 ? styles.paymentOptionDisabled : {})
          }} 
          onClick={() => { if (totalAmount <= 10000) setPaymentMethod('COD'); }}
        >
          <input type="radio" checked={paymentMethod === 'COD'} readOnly disabled={totalAmount > 10000} style={styles.radioInput} />
          <div style={styles.codTextWrapper}>
            <span style={styles.paymentOptionText}>Cash on Delivery</span>
            {totalAmount > 10000 && <span style={styles.disabledSubText}>Not available &gt; ₹10k</span>}
          </div>
        </div>
      </div>
    </div>

    {/* 3. Action Button */}
    <button 
      type="button" 
      style={{...styles.quotePayBtn, opacity: isProcessingPayment ? 0.7 : 1}} 
      onClick={handleCheckout} 
      disabled={isProcessingPayment}
    >
      {isProcessingPayment ? 'Initiating Secure Payment...' : 'Accept & Proceed to Pay'}
    </button>
    
  </div>
)}

                {/* Itemized Specification List[cite: 48] */}
                <div style={styles.itemsSummaryBox}>
                  <div style={styles.itemsHeader}>
                    <span>Items Specification ({activeRfq.total_items || items.length})</span>
                  </div>

                  <div style={styles.itemsList}>
                    {items.map((item, idx) => {
                      const itemName = item.item_name || 'Requested Construction Item';
                      const quantity = item.quantity || 1;
                      const unit = item.UOM || item.uom || 'Units';
                      const unitPrice = Number(item.price_per_unit || item.selling_price || 0);
                      const itemTotal = Number(
                        item.estimated_item_total ||
                          item.total ||
                          item.item_total_after_savings ||
                          unitPrice * quantity
                      );

                      const imageUrl = item.item_image
                        ? `${IMAGE_BASE_URL}${item.item_image}`
                        : null;

                      return (
                        <div key={idx} style={styles.itemRow}>
                          <div style={styles.itemThumbnailBox}>
                            {imageUrl ? (
                              <img src={imageUrl} alt={itemName} style={styles.itemThumbnailImg} />
                            ) : (
                              <img
                                src={ToteIcon}
                                alt="fallback"
                                style={{ width: 22, height: 22, opacity: 0.35 }}
                              />
                            )}
                          </div>

                          <div style={styles.itemMetaCol}>
                            <span style={styles.itemNameText}>{itemName}</span>
                            <span style={styles.itemQuantityText}>
                              Qty: {quantity} {unit}
                              {unitPrice > 0 &&
                                ` · ₹${unitPrice.toLocaleString('en-IN')} / ${unit}`}
                            </span>
                          </div>

                          <div style={styles.itemPriceCol}>
                            <span style={styles.itemTotalText}>
                              ₹{itemTotal.toLocaleString('en-IN')}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Estimation Price Breakdown[cite: 48] */}
                  <div style={styles.priceBreakdown}>
                    <div style={styles.breakdownRow}>
                      <span>Subtotal</span>
                      <span>₹{(activeRfq.subtotal || totalAmount).toLocaleString('en-IN')}</span>
                    </div>

                    {Number(activeRfq.tax_amount) > 0 && (
                      <div style={styles.breakdownRow}>
                        <span>Estimated Tax</span>
                        <span>₹{Number(activeRfq.tax_amount).toLocaleString('en-IN')}</span>
                      </div>
                    )}

                    {Number(activeRfq.delivery_charges) > 0 && (
                      <div style={styles.breakdownRow}>
                        <span>Freight / Logistics</span>
                        <span>₹{Number(activeRfq.delivery_charges).toLocaleString('en-IN')}</span>
                      </div>
                    )}

                    <div style={styles.breakdownDivider} />

                    <div style={styles.breakdownTotalRow}>
                      <strong>Estimated Quotation Total</strong>
                      <strong style={{ color: '#C7061F' }}>
                        ₹{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </strong>
                    </div>
                  </div>
                </div>

                {/* Invoice Reference if Order Finalized[cite: 48] */}
                {activeRfq.order_number && (
                  <div style={styles.invoiceStrip}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <img src={Receipt} alt="receipt" style={{ width: 18, height: 18 }} />
                      <span style={styles.invoiceStripTitle}>Commercial Order Invoice</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={styles.invoiceNumber}>{activeRfq.order_number}</span>
                      <img src={CaretRight} alt="arrow" style={{ width: 14, height: 14 }} />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* --- Payment Status Confirmation Overlay --- */}
      {paymentStatus && (
        <div style={styles.statusModalOverlay}>
          <div style={styles.statusCard}>
            <div style={{ ...styles.statusIconCircle, backgroundColor: paymentStatus.type === 'failed' ? '#FEE2E2' : '#DCFCE7' }}>
              <img src={paymentStatus.type === 'failed' ? X : ShieldCheck} alt="status" style={{ width: 28, height: 28 }} />
            </div>
            <h3 style={{ margin: '10px 0 6px 0', fontSize: 18, color: '#2B2A29' }}>
              {paymentStatus.type === 'failed' ? 'Payment Failed' : 'Order Placed!'}
            </h3>
            <p style={{ margin: '0 0 16px 0', fontSize: 13, color: '#636C78', textAlign: 'center' }}>{paymentStatus.message}</p>
            
            <button style={{ ...styles.statusActionBtn, opacity: isProcessingPayment ? 0.7 : 1 }} disabled={isProcessingPayment} onClick={handleOrderNavigation} type="button">
              {isProcessingPayment ? 'Loading...' : paymentStatus.type === 'failed' ? 'Retry Payment' : 'View Order'}
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

const styles = {
  container: { display: 'flex', flexDirection: 'column', gap: 20, width: '100%' },
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid #E2E8F0', paddingBottom: 16 },
  headerTitle: { margin: 0, fontSize: 22, fontWeight: '800', color: '#2B2A29' },
  headerSubtitle: { fontSize: 13, color: '#707B8C', marginTop: 4, display: 'block' },
  splitLayout: { display: 'grid', gridTemplateColumns: '340px 1fr', gap: 20, alignItems: 'flex-start' },
  masterListPane: { display: 'flex', flexDirection: 'column', gap: 10 },
  listSectionLabel: { fontSize: 12, fontWeight: '700', color: '#707B8C', textTransform: 'uppercase', letterSpacing: '0.4px' },
  cardsStack: { display: 'flex', flexDirection: 'column', gap: 10, maxHeight: '75vh', overflowY: 'auto', paddingRight: 4 },
  rfqCard: { borderRadius: 12, borderWidth: 1, borderStyle: 'solid', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8, cursor: 'pointer', transition: 'all 0.15s ease' },
  rfqCardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  rfqIdBadge: { fontSize: 13, fontWeight: '700', color: '#2B2A29' },
  statusTag: { fontSize: 11, fontWeight: '700', padding: '2px 8px', borderRadius: 999 },
  rfqCardDate: { fontSize: 11.5, color: '#707B8C' },
  rfqCardBottom: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 2 },
  rfqCardTitle: { fontSize: 12.5, fontWeight: '600', color: '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 },
  rfqCardAmount: { fontSize: 13.5, color: '#2B2A29' },
  detailPane: { minWidth: 0 },
  detailCard: { backgroundColor: '#FFFFFF', borderRadius: 14, border: '1px solid #CBD5E1', padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.02)' },
  detailHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid #F1F5F9', paddingBottom: 16 },
  detailTitle: { margin: 0, fontSize: 20, fontWeight: '800', color: '#2B2A29' },
  detailDate: { fontSize: 12, color: '#707B8C', marginTop: 4, display: 'block' },
  detailActionGroup: { display: 'flex', alignItems: 'center', gap: 8 },
  detailHelpBtn: { display: 'flex', alignItems: 'center', gap: 6, backgroundColor: '#FFFFFF', border: '1px solid #CBD5E1', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: '600', color: '#2B2A29', textDecoration: 'none' },
quotePaymentContainer: {
    backgroundColor: '#F8FAFC',
    borderRadius: 12,
    padding: 16,
    border: '1px solid #E2E8F0',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
   quoteAlertBanner: {
    backgroundColor: '#FFF1F2',
    border: '1px solid #FECDD3',
    borderRadius: 10,
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
  },
  quoteAlertLeft: { display: 'flex', flexDirection: 'column', gap: 4 },
  quoteAlertHeader: { display: 'flex', alignItems: 'center', gap: 6 },
  starIcon: { width: 16, height: 16, filter: 'invert(16%) sepia(85%) saturate(6120%) hue-rotate(348deg) brightness(91%) contrast(106%)' },
  quoteAlertTitle: { fontSize: 13, color: '#C7061F', fontWeight: '700' },
  quoteAlertPrice: { fontSize: 20, fontWeight: '800', color: '#2B2A29' },

  paymentMethodCard: { display: 'flex', flexDirection: 'column', gap: 8 },
  paymentSectionTitle: { margin: 0, fontSize: 11, fontWeight: '700', color: '#707B8C', textTransform: 'uppercase', letterSpacing: '0.5px' },
  paymentOptionsRow: { display: 'flex', gap: 10, flexDirection: 'row' },
  
  paymentOptionCompact: { 
    flex: 1, 
    border: '1px solid #CBD5E1', 
    borderRadius: 8, 
    padding: '10px 12px', 
    display: 'flex', 
    alignItems: 'center', 
    gap: 8, 
    cursor: 'pointer', 
    backgroundColor: '#FFFFFF',
    transition: 'all 0.2s ease',
  },


  paymentOption: { flex: 1, border: '1px solid #CBD5E1', borderRadius: 8, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', backgroundColor: '#FFFFFF' },
  paymentOptionActive: { borderColor: '#C7061F', backgroundColor: '#FFF1F2', boxShadow: '0 0 0 1px #C7061F' }, 
  paymentOptionDisabled: { opacity: 0.5, cursor: 'not-allowed', backgroundColor: '#F1F5F9' },
  radioInput: { margin: 0, accentColor: '#C7061F', cursor: 'inherit', width: 16, height: 16 },
  paymentOptionText: { fontSize: 13, fontWeight: '600', color: '#2B2A29', lineHeight: '1.2' },
  codTextWrapper: { display: 'flex', flexDirection: 'column' },
  disabledSubText: { fontSize: 10, color: '#DC2626', marginTop: 2, fontWeight: '600' },
  
  quotePayBtn: { 
    width: '100%', 
    backgroundColor: '#C7061F', 
    color: '#FFFFFF', 
    border: 'none', 
    borderRadius: 8, 
    padding: '14px', 
    fontSize: 14, 
    fontWeight: '700', 
    cursor: 'pointer', 
    transition: 'opacity 0.2s',
  },
  itemsSummaryBox: { backgroundColor: '#FFFFFF', borderRadius: 12, border: '1px solid #CBD5E1', overflow: 'hidden' },
  itemsHeader: { backgroundColor: '#F8F6F2', padding: '12px 18px', borderBottom: '1px solid #CBD5E1', fontSize: 13, fontWeight: '700', color: '#2B2A29' },
  itemsList: { display: 'flex', flexDirection: 'column' },
  itemRow: { display: 'flex', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid #F1F5F9', gap: 14 },
  itemThumbnailBox: { width: 44, height: 44, borderRadius: 8, backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' },
  itemThumbnailImg: { width: '90%', height: '90%', objectFit: 'contain' },
  itemMetaCol: { display: 'flex', flexDirection: 'column', flex: 1, gap: 3, minWidth: 0 },
  itemNameText: { fontSize: 13.5, fontWeight: '600', color: '#2B2A29' },
  itemQuantityText: { fontSize: 12, color: '#707B8C' },
  itemPriceCol: { flexShrink: 0 },
  itemTotalText: { fontSize: 14, fontWeight: '700', color: '#2B2A29' },
  priceBreakdown: { padding: '16px 18px', backgroundColor: '#FFFFFF', display: 'flex', flexDirection: 'column', gap: 8 },
  breakdownRow: { display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#707B8C' },
  breakdownDivider: { height: 1, backgroundColor: '#CBD5E1', margin: '4px 0' },
  breakdownTotalRow: { display: 'flex', justifyContent: 'space-between', fontSize: 15, color: '#2B2A29' },
  loadingBox: { padding: '48px 0', textAlign: 'center', color: '#707B8C', fontSize: 13.5 },
  emptyBox: { backgroundColor: '#FFFFFF', borderRadius: 14, border: '1px dashed #CBD5E1', padding: '48px 24px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  emptyIconCircle: { width: 48, height: 48, borderRadius: '50%', backgroundColor: '#FFF1F2', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 },
  emptyTitle: { margin: 0, fontSize: 16, fontWeight: '700', color: '#2B2A29' },
  emptySubtitle: { margin: '6px 0 0 0', fontSize: 12.5, color: '#707B8C', maxWidth: 320, lineHeight: '18px' },
  detailLoading: { padding: 60, textAlign: 'center', color: '#707B8C', fontSize: 13 },
  detailEmpty: { padding: 60, textAlign: 'center', color: '#707B8C', fontSize: 13, backgroundColor: '#FFFFFF', borderRadius: 14, border: '1px dashed #CBD5E1' },
  statusModalOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  statusCard: { backgroundColor: '#FFFFFF', borderRadius: 12, padding: 24, width: '100%', maxWidth: 320, display: 'flex', flexDirection: 'column', alignItems: 'center' },
  statusIconCircle: { width: 56, height: 56, borderRadius: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6 },
  statusActionBtn: { backgroundColor: '#E9001E', color: '#FFFFFF', border: 'none', borderRadius: 8, padding: '10px 20px', width: '100%', fontWeight: '700', cursor: 'pointer' },
};

/* const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    width: '100%',
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: '1px solid #E2E8F0',
    paddingBottom: 16,
  },
  headerTitle: {
    margin: 0,
    fontSize: 22,
    fontWeight: '800',
    color: '#2B2A29',
  },
  headerSubtitle: {
    fontSize: 13,
    color: '#707B8C',
    marginTop: 4,
    display: 'block',
  },
  refreshBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: '#FFFFFF',
    border: '1px solid #CBD5E1',
    borderRadius: 8,
    padding: '7px 12px',
    fontSize: 12,
    fontWeight: '600',
    color: '#2B2A29',
    cursor: 'pointer',
    outline: 'none',
  },
  splitLayout: {
    display: 'grid',
    gridTemplateColumns: '340px 1fr',
    gap: 20,
    alignItems: 'flex-start',
  },
  masterListPane: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  listSectionLabel: {
    fontSize: 12,
    fontWeight: '700',
    color: '#707B8C',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  cardsStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    maxHeight: '75vh',
    overflowY: 'auto',
    paddingRight: 4,
  },
  rfqCard: {
    borderRadius: 12,
    borderWidth: 1,
    borderStyle: 'solid',
    padding: '14px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  rfqCardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  rfqIdBadge: {
    fontSize: 13,
    fontWeight: '700',
    color: '#2B2A29',
  },
  statusTag: {
    fontSize: 11,
    fontWeight: '700',
    padding: '2px 8px',
    borderRadius: 999,
  },
  rfqCardDate: {
    fontSize: 11.5,
    color: '#707B8C',
  },
  rfqCardBottom: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 2,
  },
  rfqCardTitle: {
    fontSize: 12.5,
    fontWeight: '600',
    color: '#475569',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 200,
  },
  rfqCardAmount: {
    fontSize: 13.5,
    color: '#2B2A29',
  },
  detailPane: {
    minWidth: 0,
  },
  detailCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    border: '1px solid #CBD5E1',
    padding: '24px 28px',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
  },
  detailHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: '1px solid #F1F5F9',
    paddingBottom: 16,
  },
  detailTitle: {
    margin: 0,
    fontSize: 20,
    fontWeight: '800',
    color: '#2B2A29',
  },
  detailDate: {
    fontSize: 12,
    color: '#707B8C',
    marginTop: 4,
    display: 'block',
  },
  detailActionGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  detailHelpBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#FFFFFF',
    border: '1px solid #CBD5E1',
    borderRadius: 8,
    padding: '6px 12px',
    fontSize: 12,
    fontWeight: '600',
    color: '#2B2A29',
    textDecoration: 'none',
  },
  quoteAlertBanner: {
    backgroundColor: '#FFF1F2',
    border: '1px solid #FECDD3',
    borderRadius: 12,
    padding: '16px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  quoteAlertLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  quoteAlertHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  quoteAlertTitle: {
    fontSize: 13.5,
    color: '#C7061F',
  },
  quoteAlertPrice: {
    fontSize: 22,
    fontWeight: '800',
    color: '#2B2A29',
  },
  quotePayBtn: {
    backgroundColor: '#C7061F',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 8,
    padding: '10px 20px',
    fontSize: 13,
    fontWeight: '700',
    cursor: 'pointer',
  },
  itemsSummaryBox: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    border: '1px solid #CBD5E1',
    overflow: 'hidden',
  },
  itemsHeader: {
    backgroundColor: '#F8F6F2',
    padding: '12px 18px',
    borderBottom: '1px solid #CBD5E1',
    fontSize: 13,
    fontWeight: '700',
    color: '#2B2A29',
  },
  itemsList: {
    display: 'flex',
    flexDirection: 'column',
  },
  itemRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '14px 18px',
    borderBottom: '1px solid #F1F5F9',
    gap: 14,
  },
  itemThumbnailBox: {
    width: 44,
    height: 44,
    borderRadius: 8,
    backgroundColor: '#F8FAFC',
    border: '1px solid #E2E8F0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    overflow: 'hidden',
  },
  itemThumbnailImg: {
    width: '90%',
    height: '90%',
    objectFit: 'contain',
  },
  itemMetaCol: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    gap: 3,
    minWidth: 0,
  },
  itemNameText: {
    fontSize: 13.5,
    fontWeight: '600',
    color: '#2B2A29',
  },
  itemQuantityText: {
    fontSize: 12,
    color: '#707B8C',
  },
  itemPriceCol: {
    flexShrink: 0,
  },
  itemTotalText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#2B2A29',
  },
  priceBreakdown: {
    padding: '16px 18px',
    backgroundColor: '#FFFFFF',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  breakdownRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 13,
    color: '#707B8C',
  },
  breakdownDivider: {
    height: 1,
    backgroundColor: '#CBD5E1',
    margin: '4px 0',
  },
  breakdownTotalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 15,
    color: '#2B2A29',
  },
  invoiceStrip: {
    backgroundColor: '#F8FAFC',
    borderRadius: 10,
    border: '1px solid #E2E8F0',
    padding: '12px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  invoiceStripTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#2B2A29',
  },
  invoiceNumber: {
    fontSize: 12.5,
    color: '#64748B',
  },
  loadingBox: {
    padding: '48px 0',
    textAlign: 'center',
    color: '#707B8C',
    fontSize: 13.5,
  },
  emptyBox: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    border: '1px dashed #CBD5E1',
    padding: '48px 24px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  emptyIconCircle: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    backgroundColor: '#FFF1F2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
  },
  emptyTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: '700',
    color: '#2B2A29',
  },
  emptySubtitle: {
    margin: '6px 0 0 0',
    fontSize: 12.5,
    color: '#707B8C',
    maxWidth: 320,
    lineHeight: '18px',
  },
  detailLoading: {
    padding: 60,
    textAlign: 'center',
    color: '#707B8C',
    fontSize: 13,
  },
  detailEmpty: {
    padding: 60,
    textAlign: 'center',
    color: '#707B8C',
    fontSize: 13,
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    border: '1px dashed #CBD5E1',
  },
}; */